import { useCallback, useRef, useState } from 'react';

interface UseTapTempoReturn {
  tapTempo: () => void;
  isTapping: boolean;
  tapCount: number;
  resetTaps: () => void;
}

export const useTapTempo = (
  onBPMChange: (bpm: number) => void,
  minBPM: number = 40,
  maxBPM: number = 300
): UseTapTempoReturn => {
  const tapTimes = useRef<number[]>([]);
  const [isTapping, setIsTapping] = useState<boolean>(false);
  const [tapCount, setTapCount] = useState<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTaps = useCallback(() => {
    tapTimes.current = [];
    setTapCount(0);
    setIsTapping(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const tapTempo = useCallback(() => {
    const now = Date.now();
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add current tap time
    tapTimes.current.push(now);
    
    // Keep only the last 8 taps for calculation
    if (tapTimes.current.length > 8) {
      tapTimes.current = tapTimes.current.slice(-8);
    }

    setTapCount(tapTimes.current.length);
    setIsTapping(true);

    // Calculate BPM if we have at least 2 taps
    if (tapTimes.current.length >= 2) {
      const intervals: number[] = [];
      
      // Calculate intervals between consecutive taps
      for (let i = 1; i < tapTimes.current.length; i++) {
        const interval = tapTimes.current[i] - tapTimes.current[i - 1];
        intervals.push(interval);
      }

      // Calculate average interval
      const averageInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
      
      // Convert to BPM (60000ms = 1 minute)
      const calculatedBPM = Math.round(60000 / averageInterval);
      
      // Clamp BPM to valid range
      const clampedBPM = Math.max(minBPM, Math.min(maxBPM, calculatedBPM));
      
      // Update BPM
      onBPMChange(clampedBPM);
    }

    // Reset taps after 3 seconds of inactivity
    timeoutRef.current = setTimeout(() => {
      resetTaps();
    }, 3000);
  }, [onBPMChange, minBPM, maxBPM, resetTaps]);

  return {
    tapTempo,
    isTapping,
    tapCount,
    resetTaps,
  };
};
